import { cn } from '../../lib/utils';
import { alertVariants, variantIcons, variantIconColors } from './variants';
import { Button } from '../Button/Button';
import { X } from 'lucide-react';
import type {
  AlertProps,
  AlertWrapperProps,
  AlertTitleProps,
  AlertDescriptionProps,
} from './types';
import useI18n from '../../hooks/useI18n';

function AlertWrapper({ className, variant, ...rest }: AlertWrapperProps) {
  return (
    <div
      data-slot='alert'
      role='alert'
      className={cn(alertVariants({ variant }), className)}
      {...rest}
    />
  );
}

function AlertTitle({ className, ...rest }: AlertTitleProps) {
  return (
    <div
      data-slot='alert-title'
      className={cn(
        'eye:headline-xs',
        'eye:tracking-tight eye:col-start-2',
        className,
      )}
      {...rest}
    />
  );
}

function AlertDescription({ className, ...props }: AlertDescriptionProps) {
  return (
    <div
      data-slot='alert-description'
      className={cn(
        'eye:body-m',
        'eye:col-start-2 eye:grid eye:justify-items-start eye:gap-1',
        className,
      )}
      {...props}
    />
  );
}

function Alert({
  children,
  icon,
  onClose,
  title,
  variant,
  ...rest
}: AlertProps) {
  const { t } = useI18n();
  const severity = variant ?? 'info';
  const Icon = icon ?? variantIcons[severity];

  return (
    <AlertWrapper variant={severity} {...rest}>
      {onClose && (
        <Button
          size='icon'
          variant='ghost'
          onClick={onClose}
          className='eye:absolute eye:right-[0] eye:m-4xs'
          data-testid='alert-close-button'
        >
          <X aria-label={t('close')} />
        </Button>
      )}
      {Icon && (
        <Icon
          data-testid='alert-leading-icon'
          className={cn(
            'eye:col-start-1 eye:-mt-[3px]',
            title ? 'eye:row-span-2' : 'eye:row-span-1',
            'eye:h-[30px] eye:w-[30px] eye:stroke-[1.5px]',
            variantIconColors[severity],
          )}
          aria-hidden
        />
      )}
      {title && (
        <AlertTitle className={cn(onClose ? 'eye:pr-s' : undefined)}>
          {title}
        </AlertTitle>
      )}
      <AlertDescription>{children}</AlertDescription>
    </AlertWrapper>
  );
}

export { Alert, AlertWrapper, AlertTitle, AlertDescription };
